import { PageContainer } from '@/components/PageContainer';
import { trpc } from '@/lib/trpc';
import { Button } from '@televet/kibble-ui/Button';
import { Heading } from '@televet/kibble-ui/Heading';
import { Text } from '@televet/kibble-ui/Text';
import { Card } from '@televet/kibble-ui/Card';
import { Tag } from '@televet/kibble-ui/Tag';
import { Flex, Table, VStack, Box } from '@televet/kibble-ui/chakra';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  createFileRoute,
  useNavigate,
  Outlet,
  useLocation,
} from '@tanstack/react-router';
import { toaster } from '@/components/ui/toaster';
import { useState } from 'react';
import { DeleteDialog } from '@/components/DeleteDialog';
import { TestSuiteForm } from '@/components/forms/TestSuiteForm';
import {
  TestCaseDialog,
  type TestCaseFormData,
} from '@/components/forms/TestCaseDialog';
import {
  ExpectationDialog,
  type ExpectationFormData,
} from '@/components/forms/ExpectationDialog';
import { AttachExpectationDialog } from '@/components/forms/AttachExpectationDialog';

export const Route = createFileRoute('/_authenticated/test-suites/$suiteId')({
  component: RouteComponent,
  beforeLoad: () => {
    return {
      pageTitle: 'Test Suite',
    };
  },
});

function RouteComponent() {
  const { suiteId } = Route.useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const location = useLocation();

  // Check if we're on a child route (execution results)
  const isChildRoute = location.pathname.includes('/executions/');

  // If we're on a child route, render the outlet
  if (isChildRoute) {
    return <Outlet />;
  }

  const [showTestCaseModal, setShowTestCaseModal] = useState(false);
  const [showExpectationModal, setShowExpectationModal] = useState(false);
  const [editingTestCase, setEditingTestCase] =
    useState<TestCaseFormData | null>(null);
  const [editingTestCaseId, setEditingTestCaseId] = useState<string | null>(
    null
  );
  const [editingExpectation, setEditingExpectation] =
    useState<ExpectationFormData | null>(null);
  const [editingExpectationId, setEditingExpectationId] = useState<
    string | null
  >(null);
  const [selectedTestCaseId, setSelectedTestCaseId] = useState<string | null>(
    null
  );
  const [testCaseToDelete, setTestCaseToDelete] = useState<{
    id: string;
    name: string;
  } | null>(null);
  const [expectationToRemove, setExpectationToRemove] = useState<{
    id: string;
    criteria: string;
    testCaseId: string;
  } | null>(null);
  const [showAttachDialog, setShowAttachDialog] = useState(false);
  const [attachTestCaseId, setAttachTestCaseId] = useState<string | null>(null);
  const [showExecutions, setShowExecutions] = useState(false);

  const { data: suiteData } = useQuery(
    trpc.agentTest.getSuite.queryOptions({ suiteId })
  );

  const { data: executionsData } = useQuery(
    trpc.agentTest.listExecutions.queryOptions(
      { suiteId, page: 1, limit: 10 },
      { enabled: showExecutions }
    )
  );

  const suite = suiteData?.suite;

  const { mutateAsync: createTestCase, isPending: isCreatingTestCase } =
    useMutation(
      trpc.agentTest.createTestCase.mutationOptions({
        onSuccess: () => {
          toaster.success({
            title: 'Test case created successfully',
          });
          setShowTestCaseModal(false);
          setEditingTestCase(null);
          queryClient.invalidateQueries({
            queryKey: trpc.agentTest.getSuite.pathKey(),
          });
        },
        onError: (error) => {
          toaster.error({
            title: 'Failed to create test case',
            description: error.message,
          });
        },
      })
    );

  const { mutateAsync: updateTestCase, isPending: isUpdatingTestCase } =
    useMutation(
      trpc.agentTest.updateTestCase.mutationOptions({
        onSuccess: () => {
          toaster.success({
            title: 'Test case updated successfully',
          });
          setShowTestCaseModal(false);
          setEditingTestCase(null);
          queryClient.invalidateQueries({
            queryKey: trpc.agentTest.getSuite.pathKey(),
          });
        },
        onError: (error) => {
          toaster.error({
            title: 'Failed to update test case',
            description: error.message,
          });
        },
      })
    );

  const { mutate: deleteTestCase } = useMutation(
    trpc.agentTest.deleteTestCase.mutationOptions({
      onSuccess: () => {
        toaster.success({
          title: 'Test case deleted',
        });
        setTestCaseToDelete(null);
        queryClient.invalidateQueries({
          queryKey: trpc.agentTest.getSuite.pathKey(),
        });
      },
      onError: (error: any) => {
        toaster.error({
          title: 'Failed to delete test case',
          description: error.message,
        });
      },
    })
  );

  const { mutateAsync: createExpectation, isPending: isCreatingExpectation } =
    useMutation(
      trpc.agentTest.createExpectation.mutationOptions({
        onSuccess: () => {
          toaster.success({
            title: 'Expectation created successfully',
          });
          setShowExpectationModal(false);
          setEditingExpectation(null);
          setSelectedTestCaseId(null);
          queryClient.invalidateQueries({
            queryKey: trpc.agentTest.getSuite.pathKey(),
          });
        },
        onError: (error) => {
          toaster.error({
            title: 'Failed to create expectation',
            description: error.message,
          });
        },
      })
    );

  const { mutateAsync: updateExpectation, isPending: isUpdatingExpectation } =
    useMutation(
      trpc.agentTest.updateExpectation.mutationOptions({
        onSuccess: () => {
          toaster.success({
            title: 'Expectation updated successfully',
          });
          setShowExpectationModal(false);
          setEditingExpectation(null);
          setSelectedTestCaseId(null);
          queryClient.invalidateQueries({
            queryKey: trpc.agentTest.getSuite.pathKey(),
          });
        },
        onError: (error) => {
          toaster.error({
            title: 'Failed to update expectation',
            description: error.message,
          });
        },
      })
    );

  const { mutate: detachExpectation } = useMutation(
    trpc.agentTest.detachExpectation.mutationOptions({
      onSuccess: () => {
        toaster.success({
          title: 'Expectation removed from test case',
        });
        setExpectationToRemove(null);
        queryClient.invalidateQueries({
          queryKey: trpc.agentTest.getSuite.pathKey(),
        });
      },
      onError: (error: any) => {
        toaster.error({
          title: 'Failed to remove expectation',
          description: error.message,
        });
      },
    })
  );

  const { mutateAsync: attachExpectation, isPending: isAttaching } =
    useMutation(
      trpc.agentTest.attachExpectation.mutationOptions({
        onSuccess: () => {
          queryClient.invalidateQueries({
            queryKey: trpc.agentTest.getSuite.pathKey(),
          });
        },
        onError: (error) => {
          toaster.error({
            title: 'Failed to attach expectation',
            description: error.message,
          });
        },
      })
    );

  const { mutateAsync: executeSuite, isPending: isExecuting } = useMutation(
    trpc.agentTest.executeSuite.mutationOptions({
      onSuccess: (data) => {
        toaster.success({
          title: 'Test execution started',
          description: `Running ${data.totalTestCases} test cases`,
        });
        queryClient.invalidateQueries({
          queryKey: trpc.agentTest.listExecutions.pathKey(),
        });
        navigate({
          to: `/test-suites/${suiteId}/executions/${data.execution.id}`,
        });
      },
      onError: (error) => {
        toaster.error({
          title: 'Failed to execute test suite',
          description: error.message,
        });
      },
    })
  );

  const handleEditTestCase = (testCase: any) => {
    setEditingTestCase({
      name: testCase.name,
      description: testCase.description || '',
      inputPrompt: testCase.inputPrompt,
      sequence: testCase.sequence,
    });
    setEditingTestCaseId(testCase.id);
    setShowTestCaseModal(true);
  };

  const handleEditExpectation = (expectation: any) => {
    setEditingExpectation({
      type: expectation.type,
      criteria: expectation.criteria,
      description: expectation.description || '',
    });
    setEditingExpectationId(expectation.id);
    setShowExpectationModal(true);
  };

  const onTestCaseSubmit = async (data: TestCaseFormData) => {
    try {
      if (editingTestCaseId) {
        await updateTestCase({
          testCaseId: editingTestCaseId,
          name: data.name,
          description: data.description || undefined,
          inputPrompt: data.inputPrompt,
          sequence: data.sequence,
        });
      } else {
        await createTestCase({
          suiteId,
          name: data.name,
          description: data.description,
          inputPrompt: data.inputPrompt,
          sequence: data.sequence,
        });
      }
    } catch (error) {
      // Error is already handled by mutation onError callback
      console.error('Error submitting test case:', error);
    }
  };

  const onExpectationSubmit = async (data: ExpectationFormData) => {
    if (editingExpectationId) {
      await updateExpectation({
        expectationId: editingExpectationId,
        ...data,
      });
    } else {
      if (!selectedTestCaseId) return;
      await createExpectation({
        testCaseIds: [selectedTestCaseId],
        ...data,
      });
    }
  };

  const handleAttachExpectations = async (expectationIds: string[]) => {
    if (!attachTestCaseId) return;
    try {
      for (const expectationId of expectationIds) {
        await attachExpectation({
          expectationId,
          testCaseId: attachTestCaseId,
        });
      }
      toaster.success({
        title: `${expectationIds.length} expectation${expectationIds.length > 1 ? 's' : ''} attached`,
      });
      setShowAttachDialog(false);
      setAttachTestCaseId(null);
    } catch {
      // Error already handled by mutation onError
    }
  };

  const handleAddExpectation = (testCaseId: string) => {
    setSelectedTestCaseId(testCaseId);
    setEditingExpectation(null);
    setEditingExpectationId(null);
    setShowExpectationModal(true);
  };

  return (
    <PageContainer>
      <Flex direction="column" gap={6} w="100%" maxW="6xl">
        <TestSuiteForm suiteId={suiteId} />

        <Card borderColor="border.default" p={2}>
          <Box px={6} pt={6}>
            <Flex justify="space-between" align="center">
              <Box>
                <Flex align="center" gap={2}>
                  <Heading size="lg">Test Cases</Heading>
                  {suite?.isSequential && (
                    <Tag label="Sequential" variant="default" />
                  )}
                </Flex>
                <Text fontSize="sm" variant="subtle" mt={1}>
                  Define test cases with input prompts and expectations
                </Text>
              </Box>
              <Flex gap={2}>
                <Button
                  iconName="plus"
                  onClick={() => {
                    setEditingTestCase(null);
                    setEditingTestCaseId(null);
                    setShowTestCaseModal(true);
                  }}
                  size="sm"
                  variant="secondary"
                >
                  Add Test Case
                </Button>
                {suite && suite.testCases.length > 0 && (
                  <Button
                    onClick={() => executeSuite({ suiteId })}
                    size="sm"
                    iconName="play"
                    loading={isExecuting}
                    variant="primaryAccent"
                  >
                    Execute Suite
                  </Button>
                )}
              </Flex>
            </Flex>
          </Box>
          <Box px={6} pb={6}>
            {suite?.testCases.length === 0 ? (
              <Card borderWidth="1px" borderStyle="dashed">
                <VStack gap={2} py={6}>
                  <Text fontSize="sm" variant="subtle">
                    No test cases configured yet
                  </Text>
                  <Text fontSize="xs" variant="subtle">
                    Add test cases to define scenarios to test
                  </Text>
                </VStack>
              </Card>
            ) : (
              <VStack gap={4} align="stretch">
                {suite?.testCases.map((testCase) => (
                  <Card key={testCase.id} borderColor="border.default">
                    <Box px={6} pt={6}>
                      <Flex justify="space-between" align="center">
                        <Box>
                          <Heading size="md">{testCase.name}</Heading>
                          {testCase.description && (
                            <Text fontSize="sm" variant="subtle" mt={1}>
                              {testCase.description}
                            </Text>
                          )}
                        </Box>
                        <Flex gap={2}>
                          <Button
                            iconName="pen"
                            variant="ghostNeutral"
                            size="sm"
                            onClick={() => handleEditTestCase(testCase)}
                          />
                          <Button
                            iconName="trashcan"
                            variant="ghostDestructive"
                            size="sm"
                            onClick={() =>
                              setTestCaseToDelete({
                                id: testCase.id,
                                name: testCase.name,
                              })
                            }
                          />
                        </Flex>
                      </Flex>
                    </Box>
                    <Box px={6} pb={6}>
                      <VStack gap={4} align="stretch">
                        <Box>
                          <Text fontSize="sm" fontWeight="medium" mb={2}>
                            Input Prompt:
                          </Text>
                          <Text fontSize="sm" variant="subtle">
                            {testCase.inputPrompt}
                          </Text>
                        </Box>

                        <Flex justify="space-between" align="center">
                          <Text fontSize="sm" fontWeight="medium">
                            Expectations ({testCase.expectations.length})
                          </Text>
                          <Flex gap={2}>
                            <Button
                              iconName="chainLink"
                              size="xs"
                              variant="secondary"
                              onClick={() => {
                                setAttachTestCaseId(testCase.id);
                                setShowAttachDialog(true);
                              }}
                            >
                              Attach Existing
                            </Button>
                            <Button
                              iconName="plus"
                              size="xs"
                              variant="secondary"
                              onClick={() => handleAddExpectation(testCase.id)}
                            >
                              Add Expectation
                            </Button>
                          </Flex>
                        </Flex>

                        {testCase.expectations.length === 0 ? (
                          <Text fontSize="xs" variant="subtle">
                            No expectations defined
                          </Text>
                        ) : (
                          <VStack gap={2} align="stretch">
                            {testCase.expectations.map((expectation) => (
                              <Card
                                key={expectation.id}
                                borderWidth="1px"
                                borderColor="border.default"
                                size="sm"
                              >
                                <Box p={6}>
                                  <Flex justify="space-between" align="start">
                                    <Box flex="1">
                                      <Flex gap={2} align="center" mb={1}>
                                        <Tag
                                          label={expectation.type}
                                          variant={
                                            expectation.type === 'Content'
                                              ? 'info'
                                              : expectation.type === 'Format'
                                                ? 'default'
                                                : expectation.type === 'Tone'
                                                  ? 'warning'
                                                  : 'success'
                                          }
                                        />
                                      </Flex>
                                      <Text fontSize="sm" fontWeight="medium">
                                        {expectation.criteria}
                                      </Text>
                                      {expectation.description && (
                                        <Text fontSize="xs" variant="subtle">
                                          {expectation.description}
                                        </Text>
                                      )}
                                    </Box>
                                    <Flex gap={1}>
                                      <Button
                                        iconName="pen"
                                        variant="ghostNeutral"
                                        size="sm"
                                        onClick={() =>
                                          handleEditExpectation(expectation)
                                        }
                                      />
                                      <Button
                                        iconName="closeCircle"
                                        size="xs"
                                        variant="ghostDestructive"
                                        onClick={() =>
                                          setExpectationToRemove({
                                            id: expectation.id,
                                            criteria: expectation.criteria,
                                            testCaseId: testCase.id,
                                          })
                                        }
                                      />
                                    </Flex>
                                  </Flex>
                                </Box>
                              </Card>
                            ))}
                          </VStack>
                        )}
                      </VStack>
                    </Box>
                  </Card>
                ))}
              </VStack>
            )}
          </Box>
        </Card>

        {suite && suite._count.testExecutions > 0 && (
          <Card borderColor="border.default" p={2}>
            <Box px={6} pt={6}>
              <Flex justify="space-between" align="center">
                <Box>
                  <Heading size="lg">Execution History</Heading>
                  <Text fontSize="sm" variant="subtle" mt={1}>
                    View past test executions
                  </Text>
                </Box>
                <Button
                  size="sm"
                  variant="secondary"
                  onClick={() => setShowExecutions(!showExecutions)}
                >
                  {showExecutions ? 'Hide' : 'Show'} History
                </Button>
              </Flex>
            </Box>
            {showExecutions && (
              <Box px={6} pb={6}>
                <Table.Root size="md">
                  <Table.Header>
                    <Table.Row>
                      <Table.ColumnHeader>Status</Table.ColumnHeader>
                      <Table.ColumnHeader>Agent</Table.ColumnHeader>
                      <Table.ColumnHeader>Started</Table.ColumnHeader>
                      <Table.ColumnHeader>Completed</Table.ColumnHeader>
                      <Table.ColumnHeader textAlign="end">
                        Actions
                      </Table.ColumnHeader>
                    </Table.Row>
                  </Table.Header>
                  <Table.Body>
                    {executionsData?.executions.map((execution) => (
                      <Table.Row key={execution.id}>
                        <Table.Cell>
                          <Tag
                            label={execution.status}
                            variant={
                              execution.status === 'Completed'
                                ? 'success'
                                : execution.status === 'Failed'
                                  ? 'error'
                                  : 'info'
                            }
                          />
                        </Table.Cell>
                        <Table.Cell>{execution.agent?.name || '—'}</Table.Cell>
                        <Table.Cell>
                          {execution.startedAt
                            ? new Date(execution.startedAt).toLocaleString()
                            : '—'}
                        </Table.Cell>
                        <Table.Cell>
                          {execution.completedAt
                            ? new Date(execution.completedAt).toLocaleString()
                            : '—'}
                        </Table.Cell>
                        <Table.Cell textAlign="end">
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() =>
                              navigate({
                                to: `/test-suites/${suiteId}/executions/${execution.id}`,
                              })
                            }
                          >
                            View Results
                          </Button>
                        </Table.Cell>
                      </Table.Row>
                    ))}
                  </Table.Body>
                </Table.Root>
              </Box>
            )}
          </Card>
        )}
      </Flex>

      {/* Test Case Modal */}
      <TestCaseDialog
        isOpen={showTestCaseModal}
        onClose={() => {
          setShowTestCaseModal(false);
          setEditingTestCase(null);
          setEditingTestCaseId(null);
        }}
        onSubmit={onTestCaseSubmit}
        initialData={editingTestCase || undefined}
        isLoading={isCreatingTestCase || isUpdatingTestCase}
      />

      {/* Expectation Modal */}
      <ExpectationDialog
        isOpen={showExpectationModal}
        onClose={() => {
          setShowExpectationModal(false);
          setEditingExpectation(null);
          setEditingExpectationId(null);
          setSelectedTestCaseId(null);
        }}
        onSubmit={onExpectationSubmit}
        initialData={editingExpectation || undefined}
        isLoading={isCreatingExpectation || isUpdatingExpectation}
      />

      <DeleteDialog
        isOpen={!!testCaseToDelete}
        title="Delete Test Case"
        description={`Are you sure you want to delete "${testCaseToDelete?.name}"? This will also delete all associated expectations.`}
        onConfirm={() => {
          if (testCaseToDelete) {
            deleteTestCase({ testCaseId: testCaseToDelete.id });
          }
        }}
        onCancel={() => setTestCaseToDelete(null)}
        isDangerous
      />

      <DeleteDialog
        isOpen={!!expectationToRemove}
        title="Remove Expectation"
        description={`Remove this expectation from the test case? The expectation will still exist globally and can be re-attached later.`}
        onConfirm={() => {
          if (expectationToRemove) {
            detachExpectation({
              expectationId: expectationToRemove.id,
              testCaseId: expectationToRemove.testCaseId,
            });
          }
        }}
        onCancel={() => setExpectationToRemove(null)}
      />

      <AttachExpectationDialog
        isOpen={showAttachDialog}
        onClose={() => {
          setShowAttachDialog(false);
          setAttachTestCaseId(null);
        }}
        onAttach={handleAttachExpectations}
        existingExpectationIds={
          suite?.testCases
            .find((tc) => tc.id === attachTestCaseId)
            ?.expectations.map((e) => e.id) || []
        }
        isLoading={isAttaching}
      />
    </PageContainer>
  );
}
